import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ptzAbsoluteMove, type PtzLimits, type PtzState } from "../api/ptz.ts";
import type { PtzDir } from "./usePtzContinuousControl.ts";

const PAN_STEP_DEG = 4;
const TILT_STEP_DEG = 3;
const ZOOM_STEP = 3;
const STEP_INTERVAL_MS = 200;

const ZERO_COUNTS: Record<PtzDir, number> = {
  left: 0,
  right: 0,
  up: 0,
  down: 0,
  zoomIn: 0,
  zoomOut: 0,
};

function wrapPanDeg(pan: number, limits: PtzLimits): number {
  const { min, max } = limits.pan;
  if (Math.abs(max - min - 360) < 1) {
    let x = pan % 360;
    if (x < 0) x += 360;
    return x;
  }
  return Math.min(max, Math.max(min, pan));
}

function stepToward(cur: PtzState, dir: PtzDir, limits: PtzLimits): PtzState {
  let { pan, tilt, zoom } = cur;
  switch (dir) {
    case "left":
      pan -= PAN_STEP_DEG;
      break;
    case "right":
      pan += PAN_STEP_DEG;
      break;
    case "up":
      tilt -= TILT_STEP_DEG;
      break;
    case "down":
      tilt += TILT_STEP_DEG;
      break;
    case "zoomIn":
      zoom += ZOOM_STEP;
      break;
    case "zoomOut":
      zoom -= ZOOM_STEP;
      break;
    default:
      break;
  }
  return {
    pan: wrapPanDeg(pan, limits),
    tilt: Math.min(limits.tilt.max, Math.max(limits.tilt.min, tilt)),
    zoom: Math.round(
      Math.min(limits.zoom.max, Math.max(limits.zoom.min, zoom)),
    ),
  };
}

export type UsePtzAbsoluteStepControlOptions = {
  cameraId: string;
  operable?: boolean;
  ptzLimits: PtzLimits | null;
  getPtz: () => PtzState;
  onResult?: (state: PtzState) => void;
  onError?: (err: unknown) => void;
};

/** Hikvision ONVIF: stepped absolute moves (same API as continuous driver). */
export function usePtzAbsoluteStepControl(
  opts: UsePtzAbsoluteStepControlOptions,
): {
  start: (dir: PtzDir) => void;
  stop: () => Promise<void>;
  moving: PtzDir | null;
  counts: Record<PtzDir, number>;
  isFull: (dir: PtzDir) => boolean;
} {
  const {
    cameraId,
    operable = true,
    ptzLimits,
    getPtz,
    onResult,
    onError,
  } = opts;

  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const getPtzRef = useRef(getPtz);
  const limitsRef = useRef(ptzLimits);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    getPtzRef.current = getPtz;
  }, [getPtz]);
  useEffect(() => {
    limitsRef.current = ptzLimits;
  }, [ptzLimits]);

  const [moving, setMoving] = useState<PtzDir | null>(null);
  const movingRef = useRef<PtzDir | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef(false);

  const clearIntervalStep = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const runStep = useCallback(async () => {
    const dir = movingRef.current;
    const limits = limitsRef.current;
    if (!dir || !limits || !cameraId || inFlightRef.current) return;

    const next = stepToward(getPtzRef.current(), dir, limits);
    inFlightRef.current = true;
    try {
      const synced = await ptzAbsoluteMove(cameraId, next);
      onResultRef.current?.(synced);
    } catch (err) {
      onErrorRef.current?.(err);
    } finally {
      inFlightRef.current = false;
    }
  }, [cameraId]);

  const stop = useCallback(async (): Promise<void> => {
    clearIntervalStep();
    movingRef.current = null;
    setMoving(null);
  }, [clearIntervalStep]);

  const start = useCallback(
    (dir: PtzDir) => {
      if (!operable || !ptzLimits || !cameraId) return;
      if (movingRef.current === dir) {
        void runStep();
        return;
      }
      movingRef.current = dir;
      setMoving(dir);
      void runStep();
      clearIntervalStep();
      intervalRef.current = setInterval(() => {
        void runStep();
      }, STEP_INTERVAL_MS);
    },
    [cameraId, clearIntervalStep, operable, ptzLimits, runStep],
  );

  useEffect(
    () => () => {
      clearIntervalStep();
      movingRef.current = null;
    },
    [clearIntervalStep],
  );

  const counts = useMemo(() => {
    if (!moving) return ZERO_COUNTS;
    return { ...ZERO_COUNTS, [moving]: 1 };
  }, [moving]);

  const isFull = useCallback(() => false, []);

  return useMemo(
    () => ({ start, stop, moving, counts, isFull }),
    [start, stop, moving, counts, isFull],
  );
}
